import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  TextField,
  Typography
} from '@material-ui/core';
import Page from '../../components/Page';
import { VisitorStickerPrint } from '../../components';
import { NetworkContext } from '../../context/NetworkContext';

import { Header } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3)
  },
  scanCard: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  scanField: {
    marginTop: theme.spacing(2)
  },
  preview: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  error: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main
  },
  hint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary
  }
}));

export const PrintSticker = withRouter(() => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visitor, setVisitor] = useState(null);
  const { sendNetworkRequest } = React.useContext(NetworkContext);

  const focusInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  const resetForNextScan = useCallback(() => {
    setCode('');
    setVisitor(null);
    setError('');
    focusInput();
  }, [focusInput]);

  const lookupVisitor = useCallback(async (rawCode) => {
    const trimmed = (rawCode || '').trim();
    if (!trimmed) {
      setError('Enter or scan a registration code');
      setVisitor(null);
      return;
    }

    setLoading(true);
    setError('');
    setVisitor(null);

    const response = await sendNetworkRequest(
      `/admin/visitor-registration/${encodeURIComponent(trimmed)}`,
      {},
      undefined,
      false,
      true
    );

    setLoading(false);

    if (!response || response.statusCode === 404 || !response.visitor) {
      setError('Registration not found. Check the code and try again.');
      focusInput();
      return;
    }

    setVisitor(response.visitor);
  }, [sendNetworkRequest, focusInput]);

  function handleCodeChange(event) {
    setCode(event.target.value);
    if (error) {
      setError('');
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      lookupVisitor(code);
    }
  }

  function handleLookupClick() {
    lookupVisitor(code);
  }

  return (
    <Page className={classes.root} title="Print Sticker">
      <Header />
      <Card className={classes.scanCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scan registration QR
          </Typography>
          <Typography className={classes.hint} variant="body2">
            Point the scanner at the visitor QR code, or type the registration code and press Enter.
          </Typography>
          <TextField
            className={classes.scanField}
            fullWidth
            inputRef={inputRef}
            label="Registration code"
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            placeholder="MKB-VIS-..."
            value={code}
            variant="outlined"
            disabled={loading}
            autoComplete="off"
          />
          {error && (
            <Typography className={classes.error} variant="body2">
              {error}
            </Typography>
          )}
          <Button
            color="primary"
            disabled={loading || !code.trim()}
            onClick={handleLookupClick}
            style={{ marginTop: 16 }}
            variant="contained"
          >
            {loading ? 'Looking up…' : 'Look up'}
          </Button>
        </CardContent>
      </Card>

      {visitor && (
        <div className={classes.preview}>
          <Typography gutterBottom variant="subtitle1">
            {visitor.registration_code}
          </Typography>
          <VisitorStickerPrint
            contactname={visitor.contactname}
            companyname={visitor.companyname}
            onClear={resetForNextScan}
            onPrinted={resetForNextScan}
          />
        </div>
      )}
    </Page>
  );
});

export default PrintSticker;
